import i18n from '@/utils/i18n';
import {
  Catalog_CategoryItemsQuery,
  Catalog_CategoryItemsQueryVariables,
} from '@/types';
import {
  CatalogCategoryType,
  ItemListOption,
} from '@/contexts/item-context-types';
import { compareByCategoryName } from './use-catalog-variant-pack-groups';
import { getCategoryImageURL, NULL_CATEGORY_VALUE } from '@/utils/category';
import { gql } from '@apollo/client';
import {
  groupItemListOptions,
  useAdaptVariantPackToItemListOption,
} from '@/contexts/item-context-utils';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigValue,
} from './use-dynamic-config-value';
import {
  useItemDisplayOptions,
  useNewOrderContext,
} from '@/contexts/new-order-context';
import { useItemQueryVariables } from './use-item-query-variables';
import { useLimitedPerformanceEnabled } from './use-limited-performance';
import { useMemo } from 'react';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from './use-wrapped-query';
import { VARIANTS_PACK_FRAGMENT } from '@/contexts/items-context-queries';

interface CategoryItemsGroup {
  category: CatalogCategoryType;
  categoryItemCount: number;
  items: ItemListOption[];
}

export const CATALOG_CATEGORY_ITEMS_QUERY = gql`
  query Catalog_CategoryItems(
    $itemLimit: Int!
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
  ) {
    getSupplierCategoryItems(
      category_item_limit: $itemLimit
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      category
      category_item_count
      variant_pack {
        ...ItemsContext_VariantsPackFields
      }
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
`;

export const useCatalogCategoryItems = ({ skip = false } = {}) => {
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const itemDisplayOptions = useItemDisplayOptions();
  const itemQueryVariables = useItemQueryVariables();
  const { restaurantUUID, supplierUUID } = useNewOrderContext();
  const { isGuestMode, selectedRestaurantRelationship } = useUserContext();
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');
  const limitedPerformanceEnabled = useLimitedPerformanceEnabled();
  const specialCharsLast = useSupplierDynamicConfigValue(
    'sort_categories_with_special_chars_last',
    false,
  );

  const { data, hasResponse } = useWrappedQuery<
    Catalog_CategoryItemsQuery,
    Catalog_CategoryItemsQueryVariables
  >(CATALOG_CATEGORY_ITEMS_QUERY, {
    context: {
      anonymous: isGuestMode,
      noBatch: true,
    },
    skip: skip || !restaurantUUID || !supplierUUID || itemGroupsEnabled,
    variables: {
      ...itemQueryVariables,
      itemLimit: limitedPerformanceEnabled ? 5 : 20,
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
    },
  });

  return useMemo(() => {
    const categoryItemsMap: {
      [category: string]: ItemListOption[];
    } = {};

    return {
      categoryItemsList: (data?.getSupplierCategoryItems || [])
        .reduce<CategoryItemsGroup[]>((result, categoryItems) => {
          const vp = categoryItems.variant_pack;
          if (!vp) {
            return result;
          }

          const categoryValue = categoryItems.category || NULL_CATEGORY_VALUE;
          const categoryName = categoryItems.category ?? i18n.t('other');

          if (!categoryItemsMap[categoryValue]) {
            categoryItemsMap[categoryValue] = [];
            result.push({
              category: {
                imageUrl: getCategoryImageURL(
                  selectedRestaurantRelationship.supplierUUID,
                  categoryName,
                ),
                name: categoryItems.category ?? null,
                uuid: categoryItems.category ?? NULL_CATEGORY_VALUE,
              },
              categoryItemCount: categoryItems.category_item_count,
              items: categoryItemsMap[categoryValue],
            });
          }

          categoryItemsMap[categoryValue].push(
            adaptVariantPackToItemListOption(vp),
          );
          return result;
        }, [])
        // Sort categories alphabetically, nulls last
        .sort((a, b) =>
          compareByCategoryName(a.category, b.category, specialCharsLast),
        )
        .map((group) => ({
          ...group,
          items: groupItemListOptions(group.items, itemDisplayOptions),
        })),
      hasResponse,
    };
  }, [
    adaptVariantPackToItemListOption,
    data?.getSupplierCategoryItems,
    hasResponse,
    itemDisplayOptions,
    selectedRestaurantRelationship.supplierUUID,
    specialCharsLast,
  ]);
};
